import { useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';

import { useRequest } from '@frontend/api';

import { MFG_QUERY_KEYS } from './constants';
import type {
  AdskMfgQueryResource,
  FusionProject,
  AdskMfgParams,
  AdskMfgQueryResponse,
  AdskMfgInfiniteQueryFunction,
} from './types';
import { adskRequest, isAdskMfgError } from './utils';

const GRAPHQL_QUERY = {
  operationName: 'GetProjectsByHub',
  query: `query GetProjectsByHub($hubId: ID!, $pagination: PaginationInput) {
    projects(hubId: $hubId, pagination: $pagination) {
      pagination {
        cursor
      }
      results {
        name
        id
        projectType
        thumbnailUrl
      }
    }
  }`,
};

type DataType = AdskMfgQueryResource<FusionProject[]>;
type ReturnType = { projects: DataType };

type Params = AdskMfgParams & {
  hubId: string | null;
};

type ListProjectsByHubQueryKey = readonly [
  typeof MFG_QUERY_KEYS.LIST_PROJECTS_BY_HUB,
  string,
];

const getListProjectsByHubQueryKey = (
  hubId: string,
): ListProjectsByHubQueryKey => [MFG_QUERY_KEYS.LIST_PROJECTS_BY_HUB, hubId];

export const useListProjectsByHub: AdskMfgInfiniteQueryFunction<
  AdskMfgQueryResponse<ReturnType>,
  [Params],
  ListProjectsByHubQueryKey,
  FusionProject
> = (
  { accessToken, refreshAccess, hubId, pagination = { cursor: null } },
  { queryOptions } = {},
) => {
  const queryClient = useQueryClient();
  const request = useRequest();
  const results = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- adskRequest should be built with a hook, pagination should be added to the query key
    queryKey: getListProjectsByHubQueryKey(hubId ?? ''),
    queryFn: ({ pageParam, signal }) =>
      adskRequest<DataType, 'projects'>(
        accessToken ?? '',
        {
          ...GRAPHQL_QUERY,
          variables: {
            hubId,
            pagination: { ...pagination, cursor: pageParam },
          },
        },
        request,
        { init: { signal } },
        (data) => !!data.projects,
        (msg) => `Failed to list projects for hubId ${hubId}: ${msg}`,
      ),
    enabled: !!accessToken && !!hubId,
    getNextPageParam: (currentPage) =>
      currentPage.data.projects.pagination.cursor === null
        ? null
        : currentPage.data.projects.pagination.cursor,
    ...queryOptions,
    onError: async (error) => {
      // If not an ADSK error, bail
      if (!isAdskMfgError(error)) return;

      // Trigger refresh on 401s
      if (error.statusCode === 401) {
        await refreshAccess();

        // Invalidate query to trigger a new request
        queryClient.invalidateQueries(
          getListProjectsByHubQueryKey(hubId ?? ''),
        );
        return;
      }

      // Run the passed in onError if provided
      queryOptions?.onError?.(error);
    },
  });

  const flatData = useMemo(
    () => results?.data?.pages?.flatMap((page) => page.data.projects.results),
    [results?.data?.pages],
  );

  const flatErrors = useMemo(
    () => results?.data?.pages?.flatMap((page) => page?.errors ?? []),
    [results?.data?.pages],
  );

  return { ...results, flatData, flatErrors };
};
